import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import type { Patient, Reference } from '@medplum/fhirtypes'
import { modals } from '@mantine/modals'

import { FaxPanel } from './FaxPanel'
import { useFaxDetail, useDeleteFax, useMarkFaxViewed } from '@/hooks/useFaxInbox'
import { useFaxPatients } from '@/hooks/useFaxPatients'
import { FAX_TRIAGE_DOCUMENT_OUTPUT_TYPE } from '@/model/middleware-types'

export function FaxDetailPage() {
  const { taskId } = useParams<{ taskId: string }>()

  if (!taskId) {
    return null
  }

  // Keyed so each fax starts fresh: a page chosen in one fax's viewer must not carry into the next.
  return <FaxContent key={taskId} taskId={taskId} />
}

function FaxContent({ taskId }: { taskId: string }) {
  const { data } = useFaxDetail(taskId)
  const navigate = useNavigate()
  const deleteFax = useDeleteFax(data)
  const markViewed = useMarkFaxViewed(data.task)
  const { data: faxPatients } = useFaxPatients(taskId)
  // Page a split row asked the viewer to jump to. The nonce remounts the embed on every click,
  // so viewing the same page again (after scrolling away) still jumps.
  const [viewerPage, setViewerPage] = useState<{ page: number; nonce: number }>()
  const viewPage = (page: number) =>
    setViewerPage((prev) => ({ page, nonce: (prev?.nonce ?? 0) + 1 }))

  useEffect(() => {
    if (data.task.status !== 'requested') return
    markViewed.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.task.id, data.task.status])

  const isTriaged = data.task.status === 'completed'
  const dr = data.diagnosticReport
  // A fax triaged as a Document has no DiagnosticReport: its title lives on the
  // fax's own DocumentReference (PD-1869).
  const triagedAsDocument =
    isTriaged && !!data.task.output?.some((o) => o.type?.text === FAX_TRIAGE_DOCUMENT_OUTPUT_TYPE)

  const handleDelete = () => {
    modals.openConfirmModal({
      title: 'Delete fax',
      children: 'Are you sure you want to delete this fax? This action cannot be undone.',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteFax.mutate(undefined, {
          // Block body + `void`: navigate() returns Promise<void> | void in react-router v7,
          // and a concise arrow would implicitly return it into a slot typed as void.
          // Behaviour is unchanged — the promise was already being discarded.
          onSuccess: () => {
            void navigate('/fax/inbox')
          },
        })
      },
    })
  }

  return (
    <div className="flex size-full flex-auto bg-gray-200">
      <div className="flex flex-auto flex-col items-center justify-center">
        <embed
          key={viewerPage?.nonce}
          className="size-full"
          src={
            data.attachment.url
              ? `${data.attachment.url}#${viewerPage ? `page=${viewerPage.page}&` : ''}navpanes=0&view=fitH`
              : undefined
          }
          type={data.attachment.contentType}
        />
      </div>
      {/* Scrolls on its own, like the inbox list: a split form outgrows the screen. */}
      <div className="flex max-h-[calc(100vh-64px)] w-xs flex-none flex-col gap-4 overflow-y-auto border-r border-l border-gray-200 bg-white p-4">
        <FaxPanel
          key={data.documentReference.id}
          documentReference={data.documentReference}
          taskId={taskId}
          disabled={isTriaged}
          defaultPatient={data.task.for as Reference<Patient> | undefined}
          defaultTitle={triagedAsDocument ? data.documentReference.description : dr?.code?.text}
          defaultReferringProvider={data.referringProvider}
          defaultLabName={dr?.performer?.[0]?.display}
          defaultDocumentType={triagedAsDocument ? 'document' : 'lab'}
          ocrData={data.ocrData}
          faxPatients={faxPatients}
          onViewPage={viewPage}
          onDelete={handleDelete}
          deleteLoading={deleteFax.isPending}
        />
      </div>
    </div>
  )
}
